import React, { useState, useEffect } from 'react'
import { ManageServerHeader } from './Header'
import { ServerCard } from './ServerCard'
import { useListServerInstances } from '@/services/server'
import { ServerViewSortKeys, ServerViewType } from './types'
import { Button } from '@/components/ui/button'
import { Link, useNavigate, useRouterState } from '@tanstack/react-router'
import { Plus, ServerOff, AlertCircle, RefreshCw } from 'lucide-react'
import { useActiveOrgId } from '@/hooks/useActiveOrgId'
import { Virtuoso } from 'react-virtuoso'
import { useServerViewStore } from '@/store/ServerViewStore'
import { ErrorMessage } from '@/components/error-message'
import { LoadingMessage } from '@/components/loading-message'
import { cn } from '@/lib/utils'

export const DashboardServers = () => {
  const { orgId: activeOrgId } = useActiveOrgId()
  const navigate = useNavigate()

  // Get state from router if available
  const routerState = useRouterState({
    select: (state) => state.location.state,
  })

  // Use Zustand store for tab state management
  const { activeTab, setActiveTab } = useServerViewStore()

  const [serverSort, onServerSortChange] = useState(
    ServerViewSortKeys.STARTED_AT_DESC
  )
  const [searchTerm, setSearchTerm] = useState('')

  // Check for tab in state when component mounts or state changes
  useEffect(() => {
    if (
      routerState &&
      typeof routerState === 'object' &&
      'serverViewType' in routerState &&
      routerState.serverViewType
    ) {
      setActiveTab(routerState.serverViewType as ServerViewType)
    }
  }, [routerState, setActiveTab])

  const {
    data, // data contains { allInstances, total }
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isRefetching,
  } = useListServerInstances({
    showAllUsers: activeTab !== ServerViewType.MY_SERVER,
    sort: serverSort,
    searchText: searchTerm,
    refetchInterval: 10000,
    staleTime: 5000,
  })

  const allServers = data?.allInstances || []
  const totalServers = data?.total || 0
  const currentCount = allServers.length

  return (
    <div className="pb-12 mt-4" data-testid="servers-page">
      <ManageServerHeader
        serverViewType={activeTab}
        onServerViewChange={setActiveTab}
        serverViewSortType={serverSort}
        onServerViewSortChange={onServerSortChange}
        onSearchChange={setSearchTerm}
      />

      {!isLoading && !isError && totalServers > 0 && (
        <div
          className="flex items-center gap-2 ml-4 my-3"
          data-testid="servers-count-section"
        >
          <div
            className="text-sm text-muted-foreground"
            data-testid="servers-count-text"
          >
            Showing {currentCount} of {totalServers} servers
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => refetch()}
            disabled={isRefetching}
            data-testid="refresh-servers-button"
          >
            <RefreshCw
              className={cn('h-4 w-4', isRefetching && 'animate-spin')}
            />
          </Button>
        </div>
      )}

      {isLoading ? (
        <div
          className="flex items-center justify-center mt-32"
          data-testid="servers-loading"
        >
          <LoadingMessage message="Loading servers..." centered />
        </div>
      ) : isError ? (
        <div
          className="flex flex-col items-center justify-center gap-4 mt-28 text-center"
          data-testid="servers-error"
        >
          <AlertCircle className="w-12 h-12 text-destructive/70" />
          <h3 className="text-2xl font-medium">Unable to load servers</h3>
          <ErrorMessage
            message="There was an error loading your server instances. Please try refreshing the page."
            className="max-w-md"
            icon={false}
          />
        </div>
      ) : allServers.length === 0 ? (
        <div
          className="flex flex-col items-center gap-10 mt-20"
          data-testid="servers-empty-state"
        >
          {searchTerm.length > 0 ? (
            <>
              <h3 className="text-2xl font-medium">No search results</h3>
              <p
                className="text-muted-foreground"
                data-testid="no-search-results-message"
              >
                No servers match your search term "{searchTerm}"
              </p>
            </>
          ) : activeTab === ServerViewType.MY_SERVER ? (
            <>
              <h3
                className="text-2xl font-medium"
                data-testid="no-servers-title"
              >
                You have no servers running
              </h3>
              <ServerOff className="w-20 h-20 text-muted-foreground/50" />
            </>
          ) : (
            <>
              <h3
                className="text-2xl font-medium"
                data-testid="no-servers-title"
              >
                No servers are currently running
              </h3>
              <ServerOff className="w-20 h-20 text-muted-foreground/50" />
            </>
          )}

          <Button
            asChild
            size="lg"
            className="gap-2 mt-4"
            data-testid="create-new-server-button"
          >
            <Link
              to="/$orgId/dashboard/servers/new"
              params={{ orgId: activeOrgId.toString() }}
            >
              <Plus className="w-5 h-5" /> Create New Server
            </Link>
          </Button>
        </div>
      ) : (
        <div className="mt-6" data-testid="servers-list">
          <Virtuoso
            useWindowScroll
            data={allServers}
            endReached={() => {
              if (hasNextPage) {
                fetchNextPage()
              }
            }}
            itemContent={(_index, server) => (
              <div
                key={server.instance_id}
                className="mb-4"
                data-testid={`server-item-${server.instance_id}`}
              >
                <ServerCard
                  key={server.instance_id}
                  metadata={server}
                  onViewDetails={() => {
                    navigate({
                      to: '/$orgId/dashboard/servers/$serverId',
                      params: {
                        orgId: activeOrgId.toString(),
                        serverId: String(server.instance_id),
                      },
                      search: { tab: 'overview' },
                    })
                  }}
                />
              </div>
            )}
            components={{
              Footer: () => (
                <div
                  className="text-center py-6"
                  data-testid="servers-list-footer"
                >
                  {isFetchingNextPage ? (
                    <LoadingMessage
                      message="Loading more servers..."
                      size="sm"
                      className="flex flex-row items-center justify-center gap-2"
                      data-testid="loading-more-servers"
                    />
                  ) : hasNextPage ? null : (
                    <p
                      className="text-sm text-muted-foreground"
                      data-testid="end-of-list"
                    >
                      End of list
                    </p>
                  )}
                </div>
              ),
            }}
          />
        </div>
      )}
    </div>
  )
}
